import { useState } from 'react';
import type { AspectTreeNode } from '../lib/api';
import styles from './AspectTree.module.css';

interface AspectTreeProps {
  aspects: AspectTreeNode[];
  selectedId: string | null;
  onSelect: (aspect: AspectTreeNode) => void;
}

export default function AspectTree({ aspects, selectedId, onSelect }: AspectTreeProps) {
  return (
    <div className={styles.tree}>
      {aspects.map((aspect) => (
        <AspectNode
          key={aspect.aspectId}
          aspect={aspect}
          selectedId={selectedId}
          onSelect={onSelect}
          level={0}
        />
      ))}
    </div>
  );
}

interface AspectNodeProps {
  aspect: AspectTreeNode;
  selectedId: string | null;
  onSelect: (aspect: AspectTreeNode) => void;
  level: number;
}

function AspectNode({ aspect, selectedId, onSelect, level }: AspectNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = aspect.children.length > 0;
  const isSelected = aspect.aspectId === selectedId;
  
  return (
    <div className={styles.node}>
      <div
        className={`${styles.nodeContent} ${isSelected ? styles.selected : ''}`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(aspect)}
      >
        {hasChildren && (
          <button
            className={styles.expandBtn}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        )}
        
        <div className={styles.nodeIcon}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        
        <span className={styles.nodeName}>{aspect.name}</span>
        
        {aspect.memoryFiles.length > 0 && (
          <span className={styles.fileCount}>{aspect.memoryFiles.length}</span>
        )}
      </div>
      
      {hasChildren && expanded && (
        <div className={styles.children}>
          {aspect.children.map((child) => (
            <AspectNode
              key={child.aspectId}
              aspect={child}
              selectedId={selectedId}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

